import { useCallback, useState } from "react"
import { useTranslation } from "react-i18next"
import { useSelector } from "react-redux"

import { buildGetErrorMessage, snackbarMutation } from "helpers/utils/mutations"
import { infoMessage } from "helpers/utils/snackbars"
import { generateRawCSVFilename } from "helpers/utils/plots"
import { formatFilenameDate } from "helpers/utils/dateUtils"

import { selectToken } from "features/store/authSlice"
import { selectCurrentOrgId } from "features/store/orgSlice"
import type { EntityType } from "types/common.types"
import type { CsvDownloadFormat, CsvDownloadGroup } from "types/csv.types"
import type { FetchedCsv } from "./telemetryCsvHelpers"
import {
  getEntityCsvFilename,
  getUrl,
  getUrlParams,
  getWeatherCsvFilename,
  getWeatherUrl,
  readCsvResponse,
  saveCsvFiles,
  settleCsvTasks,
} from "./telemetryCsvHelpers"

interface DownloadProps {
  id: number
  fromDate: string
  toDate: string
  entity?: EntityType
  groups?: CsvDownloadGroup[]
  format?: CsvDownloadFormat
}

const useTelemetryCsv = () => {
  const { t } = useTranslation()
  const token = useSelector(selectToken)
  const orgId = useSelector(selectCurrentOrgId)
  const [isLoading, setIsLoading] = useState(false)

  const apiFetch = useCallback(
    (
      id: number,
      params: { fromDate: string; toDate: string },
      entity: EntityType = "device",
      fields?: string[],
      measurementOverride?: string,
    ) => {
      const url = getUrl({ id, entity })
      const urlParams = getUrlParams({
        ...params,
        orgId,
        entity,
        fields,
        measurementOverride,
      })
      const headers = {
        authorization: `Bearer ${token}`,
      }
      return fetch(`${url}?${urlParams}`, { method: "GET", headers })
    },
    [token, orgId],
  )

  const apiFetchWeather = useCallback(
    (
      params: { fromDate: string; toDate: string },
      geohashes: string[],
      fields?: string[],
      measurement?: string,
    ) => {
      const urlParams = getUrlParams({
        ...params,
        fields,
        measurementOverride: measurement,
        geohashes,
      })
      const headers = { authorization: `Bearer ${token}` }
      return fetch(`${getWeatherUrl()}?${urlParams}`, { method: "GET", headers })
    },
    [token],
  )

  const download = useCallback(
    async ({
      id,
      fromDate,
      toDate,
      entity = "device",
      groups,
      format = "separate",
    }: DownloadProps): Promise<boolean> => {
      const from = formatFilenameDate(fromDate)
      const to = formatFilenameDate(toDate)
      const filename = generateRawCSVFilename(entity, id, from, to, t)
      const zipFilename = filename.replace(".csv", ".zip")
      const emptyMsg = (msgEntity: EntityType, msgId: number | string) =>
        t("table.CSV_NO_DATA", {
          entity: t(`entities.${msgEntity.toUpperCase()}`),
          id: msgId,
        })
      const weatherEmptyMsg = () => t("table.CSV_NO_WEATHER_DATA")

      const tasks: Array<() => Promise<FetchedCsv>> =
        groups && groups.length > 0
          ? groups.flatMap((group) => {
              const needsSuffix = groups.length > 1
              if (group.geohash) {
                const groupFilename = getWeatherCsvFilename(filename, group)
                return [
                  () =>
                    apiFetchWeather(
                      { fromDate, toDate },
                      [group.geohash!],
                      group.fields,
                      group.measurement,
                    ).then((r) => readCsvResponse(r, groupFilename, weatherEmptyMsg())),
                ]
              }
              return group.ids.map((groupId) => {
                const groupFilename = getEntityCsvFilename(
                  filename,
                  group,
                  groupId,
                  needsSuffix || group.ids.length > 1,
                )
                return () =>
                  apiFetch(
                    groupId,
                    { fromDate, toDate },
                    group.entity,
                    group.fields,
                    group.measurement,
                  ).then((r) =>
                    readCsvResponse(r, groupFilename, emptyMsg(group.entity, groupId)),
                  )
              })
            })
          : [
              () =>
                apiFetch(id, { fromDate, toDate }, entity).then((r) =>
                  readCsvResponse(r, filename, emptyMsg(entity, id)),
                ),
            ]

      const runDownload = async (): Promise<{ anyNonEmpty: boolean }> => {
        const fetched = await settleCsvTasks(tasks)
        fetched
          .filter((f) => !f.nonEmpty && f.emptyMessage)
          .forEach((f) => infoMessage(f.emptyMessage as string))
        const nonEmpty = fetched.filter((f) => f.nonEmpty)
        await saveCsvFiles(nonEmpty, format, zipFilename)
        return { anyNonEmpty: nonEmpty.length > 0 }
      }

      setIsLoading(true)
      try {
        await snackbarMutation({
          mutation: runDownload(),
          getSuccessMessage: (result?: { anyNonEmpty: boolean }) =>
            result && result.anyNonEmpty
              ? t("table.CSV_DOWNLOAD_SUCCESS", {
                  entity: t(`entities.${entity.toUpperCase()}`),
                  id,
                })
              : "",
          getErrorMessage: buildGetErrorMessage(t("table.CSV_DOWNLOAD_ERROR")),
        })
        return true
      } catch {
        // `snackbarMutation` already showed the error snackbar, and re-rejects so the
        // caller can skip its success path. Report the failure instead of rejecting:
        // this hook is driven straight from click handlers, where a rejection has no
        // one to catch it and escapes as an unhandled rejection.
        return false
      } finally {
        setIsLoading(false)
      }
    },
    [apiFetch, apiFetchWeather, t, setIsLoading],
  )
  return { download, isFetching: isLoading }
}

export default useTelemetryCsv
